import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from '@/lib/api';
import { toast } from "@/lib/toast-with-sound";
import { format } from "date-fns";
import { pt, enUS, es, fr } from "date-fns/locale";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import {
  Bell, Users, Map, Mail, Settings, Trash2, CheckCheck, 
  Loader2, ChevronLeft, ChevronRight, Check, X, ArrowRight, ExternalLink
} from "lucide-react";
import { getTranslatedValue } from "@/components/TranslatableField";
import { BACKEND_URL } from "../config";
// Map icon names to Lucide components
const ICON_MAP = {
  bell: Bell,
  users: Users,
  map: Map,
  mail: Mail,
  settings: Settings,
};

// Map language codes to date-fns locales
const DATE_LOCALES = {
  pt: pt,
  en: enUS,
  es: es,
  fr: fr,
};

export function NotificationModal({ isOpen, onClose, onUnreadCountChange }) {
  const { t, i18n } = useTranslation("notifications");
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, total_pages: 0 });
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [actionLoading, setActionLoading] = useState(false);

  const dateLocale = DATE_LOCALES[i18n.language] || pt;

  // Map link_url patterns to human-readable labels
  const getLinkLabel = useCallback((url, data) => {
    if (!url) return "";
    if (url.includes("/minhas-adesoes")) return t("link_labels.my_memberships", "Ver convites");
    if (url.includes("/meus-clubes")) return t("link_labels.my_clubs", "Gerir clube");
    if (url.match(/\/clube\//)) return t("link_labels.view_club", "Ver clube") + (data?.club_name ? ` — ${data.club_name}` : "");
    if (url.match(/\/passeio\//)) return t("link_labels.view_trip", "Ver passeio");
    if (url.includes("/embaixador")) return t("link_labels.ambassador", "Programa de Embaixadores");
    if (url.includes("/motri")) return t("link_labels.motri", "Abrir Motri");
    return t("actions.view", "Ver");
  }, [t]);

  const fetchNotifications = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const res = await api.get("/api/notifications", {
        params: { page, limit: 20 }
      });
      setNotifications(res.data.notifications || []);
      setPagination(res.data.pagination || { page: 1, limit: 20, total: 0, total_pages: 0 });
      
      // Update unread count
      if (onUnreadCountChange) {
        // Get actual unread count from API
        const countRes = await api.get("/api/notifications/unread-count");
        onUnreadCountChange(countRes.data.unread_count || 0);
      }
    } catch (error) {
      toast.error(t("modal.loading_error"));
    } finally {
      setLoading(false);
    }
  }, [onUnreadCountChange, t]);

  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
      setSelectedIds(new Set());
    }
  }, [isOpen, fetchNotifications]);

  const handleSelectAll = () => {
    if (selectedIds.size === notifications.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(notifications.map(n => n.notification_id)));
    }
  };

  const handleSelectOne = (id) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      await api.post(`/api/notifications/${notificationId}/read`, {});
      setNotifications(prev => 
        prev.map(n => n.notification_id === notificationId ? { ...n, is_read: true } : n)
      );
      // Update unread count
      const countRes = await api.get("/api/notifications/unread-count");
      onUnreadCountChange?.(countRes.data.unread_count || 0);
    } catch (error) {
      toast.error(t("actions.mark_read_error"));
    }
  };

  const handleBulkMarkAsRead = async () => {
    if (selectedIds.size === 0) return;
    
    setActionLoading(true);
    try {
      await api.post("/api/notifications/bulk/read", {
        notification_ids: Array.from(selectedIds)
      });
      
      setNotifications(prev => 
        prev.map(n => selectedIds.has(n.notification_id) ? { ...n, is_read: true } : n)
      );
      const count = selectedIds.size;
      setSelectedIds(new Set());
      toast.success(count === 1 
        ? t("actions.marked_as_read_one", { count }) 
        : t("actions.marked_as_read", { count })
      );
      
      // Update unread count
      const countRes = await api.get("/api/notifications/unread-count");
      onUnreadCountChange?.(countRes.data.unread_count || 0);
    } catch (error) {
      toast.error(t("actions.mark_read_error"));
    } finally {
      setActionLoading(false);
    }
  };

  const handleMarkAllAsRead = async () => {
    setActionLoading(true);
    try {
      await api.post("/api/notifications/mark-all-read", {});
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      toast.success(t("actions.all_marked_read"));
      onUnreadCountChange?.(0);
    } catch (error) {
      toast.error(t("actions.mark_read_error"));
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async (notificationId) => {
    try {
      await api.delete(`/api/notifications/${notificationId}`);
      setNotifications(prev => prev.filter(n => n.notification_id !== notificationId));
      toast.success(t("actions.deleted"));
      
      // Update unread count
      const countRes = await api.get("/api/notifications/unread-count");
      onUnreadCountChange?.(countRes.data.unread_count || 0);
    } catch (error) {
      toast.error(t("actions.delete_error"));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    
    setActionLoading(true);
    try {
      await api.delete("/api/notifications/bulk", {
        data: { notification_ids: Array.from(selectedIds) }
      });
      
      const count = selectedIds.size;
      setNotifications(prev => prev.filter(n => !selectedIds.has(n.notification_id)));
      setSelectedIds(new Set());
      toast.success(count === 1 
        ? t("actions.deleted_bulk_one", { count }) 
        : t("actions.deleted_bulk", { count })
      );
      
      // Update unread count
      const countRes = await api.get("/api/notifications/unread-count");
      onUnreadCountChange?.(countRes.data.unread_count || 0);
    } catch (error) {
      toast.error(t("actions.delete_error"));
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    try {
      const date = new Date(dateStr);
      const now = new Date();
      const diffMs = now - date;
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      const timeStr = format(date, "HH:mm", { locale: dateLocale });
      
      // If today
      if (diffDays === 0 && date.getDate() === now.getDate()) {
        return t("time.today_at", { time: timeStr });
      }
      // If yesterday
      const yesterday = new Date(now);
      yesterday.setDate(yesterday.getDate() - 1);
      if (date.getDate() === yesterday.getDate() && 
          date.getMonth() === yesterday.getMonth() && 
          date.getFullYear() === yesterday.getFullYear()) {
        return t("time.yesterday_at", { time: timeStr });
      }
      // Otherwise show full date and time
      const dateStr2 = format(date, "d MMM", { locale: dateLocale });
      return t("time.date_at", { date: dateStr2, time: timeStr });
    } catch {
      return "";
    }
  };

  const getIcon = (iconName) => {
    const IconComponent = ICON_MAP[iconName] || Bell;
    return IconComponent;
  };

  // Helper to safely extract string from translatable field or plain string
  const extractString = (field) => {
    if (!field) return "";
    if (typeof field === "string") return field;
    if (typeof field === "object") {
      return getTranslatedValue(field, i18n.language?.split("-")[0] || "pt") || "";
    }
    return String(field);
  };

  // Translate notification title/message based on type_code and data
  const getTranslatedNotification = (notif) => {
    const typeCode = notif.type_code;
    const data = notif.data || {};
    
    // Check if we have a translation for this type
    const titleKey = `types.${typeCode}.title`;
    const messageKey = `types.${typeCode}.message`;
    
    // Try to get translated versions
    const translatedTitle = t(titleKey, data);
    const translatedMessage = t(messageKey, data);
    
    // If translation key doesn't exist (returns the key itself), use original
    const hasTranslation = translatedTitle !== titleKey;
    
    return {
      title: hasTranslation ? translatedTitle : extractString(notif.title),
      message: hasTranslation ? translatedMessage : extractString(notif.message)
    };
  };

  const unreadCount = notifications.filter(n => !n.is_read).length;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[80vh] flex flex-col" data-testid="notification-modal">
        <DialogHeader className="border-b pb-4">
          <DialogTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-primary" />
            {t("modal.title")}
            {unreadCount > 0 && (
              <Badge variant="destructive" className="ml-2">
                {t("unread_count", { count: unreadCount })}
              </Badge>
            )}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {t("modal.manage")}
          </DialogDescription>
        </DialogHeader>

        {/* Bulk Actions */}
        {notifications.length > 0 && (
          <div className="flex items-center justify-between py-2 border-b">
            <div className="flex items-center gap-2">
              <Checkbox
                checked={selectedIds.size === notifications.length && notifications.length > 0}
                onCheckedChange={handleSelectAll}
                data-testid="select-all-notifications"
              />
              <span className="text-sm text-muted-foreground">
                {selectedIds.size > 0 
                  ? (selectedIds.size === 1 
                      ? t("modal.selected_one", { count: selectedIds.size })
                      : t("modal.selected", { count: selectedIds.size })
                    )
                  : t("modal.select_all")
                }
              </span>
            </div>
            <div className="flex items-center gap-2">
              {selectedIds.size > 0 ? (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleBulkMarkAsRead}
                    disabled={actionLoading}
                    data-testid="bulk-mark-read"
                  >
                    <CheckCheck className="h-4 w-4 mr-1" />
                    {t("modal.mark_read")}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleBulkDelete}
                    disabled={actionLoading}
                    className="text-destructive hover:text-destructive"
                    data-testid="bulk-delete"
                  >
                    <Trash2 className="h-4 w-4 mr-1" />
                    {t("modal.delete")}
                  </Button>
                </>
              ) : (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleMarkAllAsRead}
                  disabled={actionLoading || unreadCount === 0}
                  data-testid="mark-all-read"
                >
                  <CheckCheck className="h-4 w-4 mr-1" />
                  {t("modal.mark_all_read")}
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Notifications List */}
        <ScrollArea className="flex-1 -mx-6 px-6">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
              <Bell className="h-12 w-12 mb-2 opacity-50" />
              <p>{t("modal.no_notifications")}</p>
            </div>
          ) : (
            <div className="space-y-1 py-2">
              {notifications.map((notif) => {
                const IconComponent = getIcon(notif.context_icon);
                const { title, message } = getTranslatedNotification(notif);
                return (
                  <div
                    key={notif.notification_id}
                    className={`
                      flex items-start gap-3 p-3 rounded-lg transition-colors
                      ${notif.is_read ? 'bg-background' : 'bg-primary/5 border-l-2 border-primary'}
                      hover:bg-muted/50
                    `}
                    data-testid={`notification-${notif.notification_id}`}
                  >
                    <Checkbox
                      checked={selectedIds.has(notif.notification_id)}
                      onCheckedChange={() => handleSelectOne(notif.notification_id)}
                      className="mt-1"
                    />
                    
                    <div className={`
                      flex items-center justify-center h-8 w-8 rounded-full flex-shrink-0
                      ${notif.is_read ? 'bg-muted' : 'bg-primary/20'}
                    `}>
                      <IconComponent className={`h-4 w-4 ${notif.is_read ? 'text-muted-foreground' : 'text-primary'}`} />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm ${notif.is_read ? 'text-muted-foreground' : 'font-medium'}`}>
                        {title}
                      </p>
                      <p className="text-sm text-muted-foreground mt-0.5">
                        {message}
                      </p>
                      <div className="flex items-center gap-2 mt-1.5">
                        <span className="text-xs text-muted-foreground">
                          {formatDate(notif.created_at)}
                        </span>
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                          {extractString(notif.context_name)}
                        </Badge>
                      </div>
                      {notif.link_url && (
                        (() => {
                          const isExternal = notif.link_url.startsWith("http://") || notif.link_url.startsWith("https://");
                          return (
                            <button
                              onClick={() => {
                                if (!notif.is_read) handleMarkAsRead(notif.notification_id);
                                if (isExternal) {
                                  window.open(notif.link_url, '_blank', 'noopener,noreferrer');
                                } else {
                                  onClose();
                                  navigate(notif.link_url);
                                }
                              }}
                              className={`mt-2 w-full flex items-center justify-between gap-2 px-3 py-2 rounded-md border text-xs font-medium transition-all group/btn ${
                                isExternal
                                  ? "bg-blue-500/10 hover:bg-blue-500/20 border-blue-500/20 text-blue-400"
                                  : "bg-primary/10 hover:bg-primary/20 border-primary/20 text-primary"
                              }`}
                              data-testid={`notification-link-${notif.notification_id}`}
                            >
                              <span>{getLinkLabel(notif.link_url, notif.data)}</span>
                              {isExternal
                                ? <ExternalLink className="h-3.5 w-3.5" />
                                : <ArrowRight className="h-3.5 w-3.5 group-hover/btn:translate-x-0.5 transition-transform" />
                              }
                            </button>
                          );
                        })()
                      )}
                    </div>
                    
                    <div className="flex items-center gap-1">
                      {!notif.is_read && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => handleMarkAsRead(notif.notification_id)}
                          title={t("actions.mark_as_read")}
                        >
                          <Check className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-muted-foreground hover:text-destructive"
                        onClick={() => handleDelete(notif.notification_id)}
                        title={t("actions.delete")}
                      >
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>

        {/* Pagination */}
        {pagination.total_pages > 1 && (
          <div className="flex items-center justify-between pt-4 border-t">
            <span className="text-sm text-muted-foreground">
              {t("modal.page_of", { current: pagination.page, total: pagination.total_pages })}
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => fetchNotifications(pagination.page - 1)}
                disabled={pagination.page <= 1 || loading}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => fetchNotifications(pagination.page + 1)}
                disabled={pagination.page >= pagination.total_pages || loading}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default NotificationModal;
